import type { TokenResponse } from '@react-oauth/google';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useFetcher } from '@remix-run/react';
import { useCallback, useEffect } from 'react';
import type { FetcherData } from 'types';

import GoogleIcon from '~/assets/icons/Google';
import useEnv from '~/hooks/useEnv';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';

import type { PostAuthOptions, ScreenProps } from '..';

type Props = {
  onSuccess: () => void;
  postAuthOptions?: PostAuthOptions;
  setError: ScreenProps['setError'];
};

type GoogleLoginSuccessResponse = Omit<
  TokenResponse,
  'error' | 'error_description' | 'error_uri'
>;

function GoogleLogin({ onSuccess, postAuthOptions, setError }: Props) {
  const { GOOGLE_LOGIN_CLIENT_ID } = useEnv();

  return (
    <GoogleOAuthProvider clientId={GOOGLE_LOGIN_CLIENT_ID}>
      <GoogleLoginButton
        onSuccess={onSuccess}
        postAuthOptions={postAuthOptions}
        setError={setError}
      />
    </GoogleOAuthProvider>
  );
}

function GoogleLoginButton({ onSuccess, postAuthOptions, setError }: Props) {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const errors = fetcher.data?.errors;

  function onLoginSuccess(response: GoogleLoginSuccessResponse) {
    fetcher.submit(
      {
        action: postAuthOptions?.action ?? '',
        guestId: user.guestId ?? '',
        legacyProductId: postAuthOptions?.legacyProductId ?? '',
        redirectUrl: postAuthOptions?.redirectUrl ?? '',
        sizes: JSON.stringify(postAuthOptions?.sizes ?? []),
        token: response.access_token
      },
      { action: '/bolt/login/google', method: 'post' }
    );

    gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_GOOGLE',
      pagePosition: 'Sign in modal'
    });
  }

  const onSignIn = useCallback(() => {
    gtm.sendLoginEvent(user.id, LoginSource.Google);

    braze.setUser(user.id);
    braze.logLoginEvent();

    onSuccess();
  }, [onSuccess, user.id]);

  const errorMessage =
    'Google sign in failed. If on a social media browser, please open this page in your external browser. If not, please try again or use another method.';

  const handleLogin = useGoogleLogin({
    onError: () => setError(errorMessage),
    onNonOAuthError: () => setError(errorMessage),
    onSuccess: onLoginSuccess
  });

  function onLoginClick() {
    setError(null);
    handleLogin();
  }

  useEffect(() => {
    if (errors?.message) setError(errors.message);
  }, [errors, setError]);

  useEffect(() => {
    if (fetcher.data?.success) onSignIn();
  }, [fetcher.data, onSignIn]);

  return (
    <button
      className="flex h-12 w-full items-center justify-center gap-2 rounded-lg  bg-concrete px-4 py-2 font-semibold"
      onClick={onLoginClick}
    >
      <GoogleIcon size={22} />
      <span className="hidden md:inline-block">Google</span>
    </button>
  );
}

export default GoogleLogin;
